import { v4 as uuidv4 } from "uuid";
import { useState, useEffect, useContext, useRef, FormEvent } from "react";
import { useCustomersService } from "pages/dashboard/customers/service";
import { SignalContext } from "context/SignalContext";
import { showToast } from "ui/components/common";
import { IPSpeakerSearchDataType, WsResDataType } from "lib/types";
import {
  SvgBoxChecked,
  SvgBoxUnChecked,
  SvgEdit,
  SvgEye,
  SvgUnEye,
  SvgToastInfo,
} from "ui/svgs";
import { aesEncrypt } from "lib/utils";
import clsx from "clsx";
import { Tooltip } from "antd";

export default function IPSpeakerSearchItem({
  orgId,
  IPSpeakerData,
  onSelect,
  selected,
  hasError,
}: {
  orgId: string;
  IPSpeakerData: IPSpeakerSearchDataType;
  selected: boolean;
  hasError: boolean;
  onSelect?: (speaker: IPSpeakerSearchDataType, selected: boolean) => void;
}) {
  const { sendValidateDeviceRequest } = useCustomersService();

  const { sessionId } = useContext(SignalContext);

  const inputRef = useRef<HTMLInputElement>(null);
  const [IPSpeaker, setIPSpeaker] = useState<IPSpeakerSearchDataType>();
  const [nameEditing, setNameEditing] = useState(false);
  const [pwsVisible, setPwsVisible] = useState(false);
  const [validateFailed, setValidateFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIPSpeaker(IPSpeakerData);
  }, [IPSpeakerData]);

  useEffect(() => {
    if (nameEditing) {
      inputRef?.current?.focus();
    }
  }, [nameEditing]);

  const validateIPSpeaker = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (IPSpeaker?.status !== "un_authed") {
      return;
    }
    setLoading(true);
    setValidateFailed(false);
    const postBody: { username?: string; password?: string } = {};
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    formData.forEach((value, property: string) => (postBody[property] = value));

    try {
      const iv = uuidv4({}).slice(0, 16);

      const key = sessionId.slice(0, 32);
      const req = {
        nvrId: IPSpeakerData.nvrId,
        macAddress: IPSpeakerData.macAddress,
        account: {
          username: postBody.username,
          password: aesEncrypt(postBody.password, key, iv),
        },
        deviceType: IPSpeakerData.deviceType,
        manufacturer: IPSpeakerData.manufacturer,
      };

      const res = await sendValidateDeviceRequest(req, orgId);
      const data = (res as WsResDataType)?.deviceValidateReply.speakerList;
      const cur = data?.find(
        (res) => res.macAddress === IPSpeakerData.macAddress,
      );

      if (!cur) {
        return;
      }
      if (cur.error) {
        setValidateFailed(true);
      } else {
        setIPSpeaker({
          ...cur,
          account: {
            username: postBody.username,
            password: aesEncrypt(postBody.password, sessionId.slice(0, 32), iv),
          },
        });
      }
    } catch (error) {
      setValidateFailed(true);
      showToast(
        1,
        "Failed to validate device. Please try again later. Error" + error,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        "mb-3 px-6 py-2 w-full rounded-2xl bg-slate-50 border-2 group border-slate-50",
        {
          "hover:border-blue-500": IPSpeaker?.status !== "un_authed",
        },
      )}
    >
      <div>
        <div className="flex">
          <button
            className={clsx(
              "ml-1 mr-[10px] overflow-hidden w-6",
              IPSpeaker?.status === "un_authed" ? "opacity-25" : "opacity-100",
            )}
            disabled={IPSpeaker?.status === "un_authed"}
            onClick={() => {
              onSelect(IPSpeaker, !selected);
            }}
          >
            {selected ? (
              <SvgBoxChecked width="18" className="fill-neutral-500 mr-1" />
            ) : (
              <SvgBoxUnChecked width="18" className="fill-neutral-500 mr-1" />
            )}
          </button>
          <div className="text-base" style={{ lineHeight: "22px" }}>
            <div>
              {nameEditing ? (
                <input
                  className="rounded-lg px-3 py-1 focus:border-blue-500 border-blue-500 focus-visible:border-blue-500 focus-visible:outline-none border"
                  ref={inputRef}
                  style={{
                    width: IPSpeaker?.name?.length + 4 + "ch",
                    maxWidth: "500px",
                  }}
                  value={IPSpeaker.name}
                  maxLength={32}
                  onBlur={() => {
                    setNameEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setNameEditing(false);
                    }
                  }}
                  onChange={(e) => {
                    setIPSpeaker({ ...IPSpeaker, name: e.target.value });
                  }}
                />
              ) : (
                <div className="flex item-center py-1">
                  <span className="max-w-md overflow-hidden break-words text-nowrap text-ellipsis">
                    {IPSpeaker?.name}
                  </span>
                  {IPSpeaker?.status !== "un_authed" && (
                    <button
                      className="invisible group-hover:visible ml-3 rounded-full p-1 hover:bg-[#DBE1EE]"
                      onClick={() => {
                        setNameEditing(true);
                      }}
                    >
                      <SvgEdit width="17" className="fill-neutral-500 " />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="text-neutral-500">
              {(IPSpeaker?.manufacturer || "") + " - "} [{IPSpeaker?.macAddress}
              ]
            </div>
            {(IPSpeaker?.status === "added" ||
              IPSpeaker?.status === "other_added") && (
              <div className="mt-1 text-blue-500 text-sm flex gap-1 item-center">
                <SvgToastInfo width="15" className="fill-blue-500" />
                This IP speaker has already been added to [
                {IPSpeaker?.oldNvrName}].
              </div>
            )}
            {IPSpeaker?.status === "unavailable" && (
              <div className="mt-1 text-blue-500 text-sm flex gap-1 item-center">
                <SvgToastInfo width="15" className="fill-blue-500" />
                This IP speaker is offline.
              </div>
            )}
          </div>
        </div>
        {IPSpeaker?.status === "un_authed" && (
          <div className="ml-10 mt-3  pt-3 border-t border-t-neutral-200">
            <div className="text-sm">
              Please enter the username and password to continue.
            </div>
            {IPSpeaker?.manufacturer === "AXIS" && (
              <div className="text-sm text-[#FF8530]">
                This will be the initial password if none has been set before.
              </div>
            )}

            <form
              className="mt-2 grid md:grid-cols-[auto,auto,111px] md:gap-3 pb-1"
              onSubmit={validateIPSpeaker}
            >
              <div className="relative h-[53px]">
                <input
                  disabled={loading}
                  name="username"
                  className="peer block w-full rounded-lg focus:border-blue-500 border-blue-500 focus-visible:border-blue-500 focus-visible:outline-none border py-4 px-5 text-sm outline-2 aria-disabled:opacity-30 aria-disabled:border-neutral-400"
                  placeholder="Enter username"
                  value={
                    IPSpeaker?.manufacturer === "AXIS" ? "root" : undefined
                  }
                  aria-disabled={IPSpeaker?.manufacturer === "AXIS"}
                  aria-describedby="username-error"
                  autoComplete="off"
                  onChange={(e) => {
                    setValidateFailed(false);
                  }}
                />
              </div>
              <div className="relative h-[53px]">
                <input
                  disabled={loading}
                  className="peer block w-full rounded-lg focus:border-blue-500 border-blue-500 focus-visible:border-blue-500 focus-visible:outline-none border py-4 px-5 text-sm outline-2 "
                  type={pwsVisible ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  aria-describedby="password-error"
                  autoComplete="new-password"
                  onChange={(e) => {
                    setValidateFailed(false);
                  }}
                />
                {pwsVisible ? (
                  <button
                    type="button"
                    onClick={() => {
                      setPwsVisible(!pwsVisible);
                    }}
                  >
                    <SvgEye
                      width="17"
                      className=" fill-neutral-500 hover:fill-neutral-400  absolute right-5 top-5"
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setPwsVisible(!pwsVisible);
                    }}
                  >
                    <SvgUnEye
                      width="17"
                      className=" fill-neutral-500 hover:fill-neutral-400  absolute right-5 top-5"
                    />
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className={clsx(
                  "max-w-[111px] h-[53px] flex justify-center items-center rounded-2xl text-white px-6 py-4 text-base font-semibold bg-blue-500 transition-colors hover:bg-blue-400 disabled:bg-blue-400 disabled:hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                  {
                    "bg-[#222222] bg-opacity-15 text-[#222222] text-opacity-45 hover:bg-[#222222] hover:bg-opacity-15 cursor-default":
                      validateFailed,
                  },
                )}
              >
                {loading ? (
                  <div
                    className="ml-1 text-white  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  ></div>
                ) : (
                  "Validate"
                )}
              </button>
            </form>
            {validateFailed && (
              <div className="mt-2 text-[#F73D2F] text-sm flex gap-1 item-center">
                <SvgToastInfo width="15" className="fill-[#F73D2F]" />
                The username and password do not match. Please try again.
              </div>
            )}
            {!validateFailed && hasError && (
              <div className="mt-2 text-[#F73D2F] text-sm flex gap-1 item-center">
                <SvgToastInfo width="15" className="fill-[#F73D2F]" />
                Unable to add this IP Speaker. Please try again.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
